import { useEffect, useRef, useState, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';

// Get WebSocket URL for canvas
const getCanvasWebSocketURL = (canvasId) => {
  const isLocal = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
  
  if (isLocal) {
    return `ws://localhost:8001/ws/canvas/${canvasId}`;
  }
  
  // Production - use the backend URL with proper prefix
  const baseUrl = process.env.REACT_APP_BACKEND_URL
    .replace('https://', 'wss://')
    .replace('http://', 'ws://');
  
  return `${baseUrl}/api/ws/canvas/${canvasId}`;
};

export function useCanvasWebSocket(canvasId, handlers = {}) {
  const { token } = useAuth();
  const [isConnected, setIsConnected] = useState(false);
  const [activeUsers, setActiveUsers] = useState([]);
  const [cursors, setCursors] = useState({});
  const [activeEdits, setActiveEdits] = useState({});
  const [userColor, setUserColor] = useState('#3B82F6');
  
  const wsRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  const reconnectAttemptsRef = useRef(0);
  const maxReconnectAttempts = 5;

  // Connect to WebSocket
  const connect = useCallback(() => {
    if (!canvasId || !token) return;
    
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      return;
    }
    
    try {
      const url = `${getCanvasWebSocketURL(canvasId)}?token=${token}`;
      const ws = new WebSocket(url);
      
      ws.onopen = () => {
        console.log('✅ Canvas WebSocket connected');
        setIsConnected(true);
        reconnectAttemptsRef.current = 0;
        
        // Ping every 30s to keep alive
        const pingInterval = setInterval(() => {
          if (ws.readyState === WebSocket.OPEN) {
            ws.send(JSON.stringify({ type: 'ping' }));
          }
        }, 30000);
        
        ws.pingInterval = pingInterval;
      };
      
      ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          handleMessage(data);
        } catch (error) {
          console.error('Error parsing canvas WebSocket message:', error);
        }
      };
      
      ws.onerror = (error) => {
        console.error('❌ Canvas WebSocket error:', error);
      };
      
      ws.onclose = (event) => {
        console.log('🔌 Canvas WebSocket closed:', event.code);
        setIsConnected(false);
        
        if (ws.pingInterval) {
          clearInterval(ws.pingInterval);
        }
        
        // Attempt reconnect
        if (reconnectAttemptsRef.current < maxReconnectAttempts) {
          reconnectAttemptsRef.current += 1;
          console.log(`🔄 Reconnecting... (Attempt ${reconnectAttemptsRef.current}/${maxReconnectAttempts})`);
          reconnectTimeoutRef.current = setTimeout(connect, 3000);
        }
      };
      
      wsRef.current = ws;
    } catch (error) {
      console.error('Error connecting canvas WebSocket:', error);
    }
  }, [canvasId, token]);

  // Handle incoming messages
  const handleMessage = useCallback((data) => {
    switch (data.type) {
      case 'init':
        setUserColor(data.user_color);
        setActiveUsers(data.active_users || []);
        setCursors(data.cursors || {});
        setActiveEdits(data.active_edits || {});
        break;
      
      case 'user_joined':
        setActiveUsers(data.active_users || []);
        break;
      
      case 'user_left':
        setActiveUsers(data.active_users || []);
        // Clean up cursor
        if (data.user?.user_id) {
          setCursors(prev => {
            const next = { ...prev };
            delete next[data.user.user_id];
            return next;
          });
        }
        break;
      
      case 'cursor_update':
        if (data.cursor) {
          setCursors(prev => ({
            ...prev,
            [data.cursor.user_id]: data.cursor
          }));
        }
        break;
      
      case 'element_created':
        handlers.onElementCreated?.(data);
        break;
      
      case 'element_updated':
        handlers.onElementUpdated?.(data);
        break;
      
      case 'element_deleted':
        handlers.onElementDeleted?.(data);
        break;
      
      case 'elements_batch_updated':
        handlers.onBatchUpdated?.(data);
        break;
      
      case 'elements_batch_deleted':
        handlers.onBatchDeleted?.(data);
        break;
      
      case 'editing_started':
        setActiveEdits(prev => ({
          ...prev,
          [data.element_id]: data.editor
        }));
        break;
      
      case 'editing_stopped':
        setActiveEdits(prev => {
          const next = { ...prev };
          delete next[data.element_id];
          return next;
        });
        break;
      
      case 'pong':
        // Keep-alive response
        break;
      
      default:
        console.log('Unknown canvas message type:', data.type);
    }
  }, [handlers]);

  // Send message helper
  const send = useCallback((message) => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify(message));
    }
  }, []);

  // Public send methods
  const sendCursorMove = useCallback((x, y) => {
    send({ type: 'cursor_move', x, y });
  }, [send]);

  const sendElementCreate = useCallback((element) => {
    send({ type: 'element_create', element });
  }, [send]);

  const sendElementUpdate = useCallback((elementId, updates) => {
    send({ type: 'element_update', element_id: elementId, updates });
  }, [send]);

  const sendElementDelete = useCallback((elementId) => {
    send({ type: 'element_delete', element_id: elementId });
  }, [send]);

  const sendBatchUpdate = useCallback((updates) => {
    send({ type: 'elements_batch_update', updates });
  }, [send]);

  const sendBatchDelete = useCallback((elementIds) => {
    send({ type: 'elements_batch_delete', element_ids: elementIds });
  }, [send]);

  const sendStartEditing = useCallback((elementId) => {
    send({ type: 'start_editing', element_id: elementId });
  }, [send]);

  const sendStopEditing = useCallback((elementId) => {
    send({ type: 'stop_editing', element_id: elementId });
  }, [send]);

  // Connect on mount
  useEffect(() => {
    connect();
    
    return () => {
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }
      if (wsRef.current) {
        wsRef.current.close();
      }
    };
  }, [connect]);

  return {
    isConnected,
    activeUsers,
    cursors,
    activeEdits,
    userColor,
    sendCursorMove,
    sendElementCreate,
    sendElementUpdate,
    sendElementDelete,
    sendBatchUpdate,
    sendBatchDelete,
    sendStartEditing,
    sendStopEditing
  };
}

export default useCanvasWebSocket;
